import React, { useMemo } from 'react';
import cn from 'clsx';

import Button from '../../library/button/button';
import ProductSwatchDescription from '../product-swatch-description/product-swatch-description';

import { useProductSwatchSelection } from '../../shared/hooks/hooks';

import { trackAction } from '../../shared/utils/analytics/analytics';
import { getProductSwatchesFilteredByImageFormat } from '../../shared/utils/product/product';

import { Maybe, SwatchImageData } from '../../shared/types/graphql-types';
import { PRODUCT_IMAGE_FORMATS } from '../../shared/constants/product-images';

import styles from './product-swatch-selection.scss';

interface ProductSwatchSelectionProps {
  availableSwatches?: SwatchImageData[];
  className?: string;
  onSwatchChange: (swatch: SwatchImageData) => void;
  productCode: string;
  productDetailsUrl: string;
  productFinish?: Maybe<string> | undefined;
  selectedSwatch?: SwatchImageData;
  showAllSwatches?: boolean;
  swatches: Maybe<Maybe<SwatchImageData>[]> | undefined;
}

function ProductSwatchSelection(props: ProductSwatchSelectionProps) {
  const {
    availableSwatches,
    className = '',
    onSwatchChange,
    productCode,
    productDetailsUrl,
    productFinish: propsProductFinish,
    selectedSwatch: propsSelectedSwatch,
    showAllSwatches = false,
    swatches
  } = props;

  const thumbnailSwatches = useMemo(
    () =>
      getProductSwatchesFilteredByImageFormat(
        swatches,
        PRODUCT_IMAGE_FORMATS.PRODUCT_X_SMALL
      ),
    [swatches]
  );

  const { saveSelectedSwatch, selectedSwatch: internalSelectedSwatch } =
    useProductSwatchSelection(productCode, swatches);
  const selectedSwatch = propsSelectedSwatch || internalSelectedSwatch;
  const productFinish = selectedSwatch?.vendorFinish || propsProductFinish;
  const availableSwatchesSet = Array.isArray(availableSwatches)
    ? new Set(availableSwatches.map(c => c?.code))
    : new Set();

  function swatchChange(swatch) {
    saveSelectedSwatch(swatch);

    if (onSwatchChange) {
      onSwatchChange(swatch);
    }
  }

  function swatchToPDP() {
    trackAction('click_swatch_plus_to_pdp');
  }

  if (!Array.isArray(swatches) || swatches.length <= 0) {
    return (
      <ProductSwatchDescription
        color={selectedSwatch?.altText}
        finish={productFinish}
      />
    );
  }

  return (
    <div
      className={cn(
        styles['container'],
        `product-swatch-selection ${className}`
      )}
    >
      <ProductSwatchDescription
        color={selectedSwatch?.altText}
        finish={productFinish}
      />
      <div className={cn(styles['swatches-container'], 'swatches-container')}>
        {thumbnailSwatches?.map((swatch, i) => {
          const { code, hexcode, linkUrl } = swatch || {};
          const safeUrl =
            typeof linkUrl === 'string' && linkUrl.trim()
              ? linkUrl.trim().replace(/\s/g, '%20')
              : '';
          const swatchId = swatch?.productCode;
          const selectedSwatchId = selectedSwatch?.productCode;
          const additonalFinishes = thumbnailSwatches.length - i;
          if (thumbnailSwatches.length > 4 && i === 3 && !showAllSwatches) {
            return (
              <Button
                href={productDetailsUrl}
                key={`swatch-${i}`}
                onClick={swatchToPDP}
              >
                <div
                  className={`${styles['swatch-plus']} ${
                    selectedSwatchId === swatchId
                      ? styles['selected']
                      : styles['not-selected']
                  }`}
                >
                  +{additonalFinishes}
                </div>
              </Button>
            );
          } else if (i < 4 || showAllSwatches) {
            return (
              <Button
                ariaLabel={swatch?.vendorFinish || ''}
                key={`swatch-${i}`}
                onClick={() => swatchChange(swatch)}
                tabIndex={0}
                type="button"
              >
                <div
                  className={`${styles['swatch']} ${
                    selectedSwatchId === swatchId
                      ? styles['selected']
                      : styles['not-selected']
                  }`}
                  style={{
                    backgroundColor: !safeUrl && hexcode ? hexcode : 'none',
                    backgroundImage: safeUrl ? `url("${safeUrl}")` : ''
                  }}
                >
                  <>
                    {availableSwatchesSet.size > 0 &&
                      code &&
                      !availableSwatchesSet.has(code) && (
                        <div className={styles['swatch-not-available']} />
                      )}
                  </>
                </div>
              </Button>
            );
          }
        })}
      </div>
    </div>
  );
}

export default ProductSwatchSelection;
